import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, Mail, Send, FileText, Users, Clock, AlertCircle, CheckCircle2,
  Award, Timer, CalendarDays, Activity, Eye, Sparkles,
} from 'lucide-react';

import { Button } from '../../components/ui/button';
import { formatApiErrorDetail } from '../../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};
const fmtDateTime = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
};
const fmtRelative = (iso) => {
  if (!iso) return 'Never';
  const diff = Date.now() - new Date(iso).getTime();
  const s = Math.floor(diff / 1000);
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  const mo = Math.floor(d / 30);
  return `${mo}mo ago`;
};
const fmtHours = (h) => {
  if (h == null) return '—';
  if (h < 1) return `${Math.round(h * 60)}m`;
  if (h < 48) return `${h.toFixed(1)}h`;
  return `${(h / 24).toFixed(1)}d`;
};

const statusBadge = (status) => {
  const map = {
    submitted: 'bg-green-100 text-green-800',
    pending: 'bg-yellow-100 text-yellow-800',
    opened: 'bg-blue-100 text-blue-800',
    revoked: 'bg-red-100 text-red-800',
    expired: 'bg-gray-100 text-gray-700',
  };
  return (
    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${map[status] || 'bg-gray-100 text-gray-700'}`}>
      {status?.toUpperCase()}
    </span>
  );
};

const KIND_META = {
  created:   { color: 'bg-[#5b93d6]', ring: 'ring-[#5b93d6]/30',  icon: Send, label: 'Invited' },
  opened:    { color: 'bg-blue-500',  ring: 'ring-blue-500/30',   icon: Eye, label: 'Verified' },
  submitted: { color: 'bg-green-600', ring: 'ring-green-600/30',  icon: CheckCircle2, label: 'Submitted' },
  revoked:   { color: 'bg-red-500',   ring: 'ring-red-500/30',    icon: AlertCircle, label: 'Revoked' },
};

/* Sparkline (SVG) */
function Sparkline({ weeks }) {
  const width = 640, height = 140;
  const padX = 30, padY = 16;
  const chartW = width - padX * 2;
  const chartH = height - padY * 2;
  const maxVal = Math.max(1, ...weeks.flatMap((w) => [w.sent, w.submitted]));

  const line = (points) => points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ');
  const areaGrad = (pts) => `${pts[0].x},${padY + chartH} ${pts.map((p) => `${p.x},${p.y}`).join(' ')} ${pts[pts.length - 1].x},${padY + chartH}`;

  const dx = weeks.length > 1 ? chartW / (weeks.length - 1) : chartW;
  const sentPts = weeks.map((w, i) => ({ x: padX + i * dx, y: padY + chartH - (w.sent / maxVal) * chartH }));
  const subPts = weeks.map((w, i) => ({ x: padX + i * dx, y: padY + chartH - (w.submitted / maxVal) * chartH }));

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-auto" data-testid="detail-sparkline">
      <defs>
        <linearGradient id="grad-sent" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#5b93d6" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#5b93d6" stopOpacity="0.02" />
        </linearGradient>
        <linearGradient id="grad-sub" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#0f2444" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#0f2444" stopOpacity="0.02" />
        </linearGradient>
      </defs>
      {[0, 0.5, 1].map((t) => {
        const y = padY + chartH * (1 - t);
        return (
          <g key={t}>
            <line x1={padX} y1={y} x2={width - padX} y2={y} stroke="#e2e8f0" strokeDasharray="2 3" />
            <text x={padX - 6} y={y + 3} textAnchor="end" fontSize="9" fill="#94a3b8">{Math.round(maxVal * t)}</text>
          </g>
        );
      })}
      <polygon fill="url(#grad-sent)" points={areaGrad(sentPts)} />
      <polygon fill="url(#grad-sub)" points={areaGrad(subPts)} />
      <path d={line(sentPts)} fill="none" stroke="#5b93d6" strokeWidth="2" />
      <path d={line(subPts)} fill="none" stroke="#0f2444" strokeWidth="2" />
      {sentPts.map((p, i) => <circle key={`s${i}`} cx={p.x} cy={p.y} r="3" fill="#5b93d6" />)}
      {subPts.map((p, i) => <circle key={`u${i}`} cx={p.x} cy={p.y} r="3" fill="#0f2444" />)}
      {weeks.map((w, i) => (
        <text key={w.week_start} x={padX + i * dx} y={height - 2} textAnchor="middle" fontSize="9" fill="#475569">{w.label}</text>
      ))}
    </svg>
  );
}

const KpiCard = ({ label, value, sub, subClass = 'text-[#475569]', testid }) => (
  <div className="bg-white rounded-lg shadow-sm border border-[#e2e8f0] p-4">
    <p className="text-xs text-[#475569] uppercase tracking-wider">{label}</p>
    <p className="text-2xl font-bold text-[#0f172a] mt-1" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid={testid}>{value}</p>
    {sub && <p className={`text-xs mt-1 ${subClass}`}>{sub}</p>}
  </div>
);

export default function AdvisorDetail() {
  const { advisorId } = useParams();
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/advisors/${advisorId}/detail`, { withCredentials: true });
        setDetail(data);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Failed to load advisor');
        navigate('/admin/dashboard');
      } finally {
        setLoading(false);
      }
    })();
  }, [advisorId, navigate]);

  const mailtoHref = useMemo(() => {
    if (!detail) return '#';
    const subject = encodeURIComponent(`SteelPeak Wealth — ${detail.advisor.name || 'Advisor'}`);
    const body = encodeURIComponent(
      `Hi ${detail.advisor.name?.split(' ')[0] || 'there'},\n\nJust checking in on your recent invite activity — you've sent ${detail.kpi.sent} invite(s) with a ${detail.kpi.submission_rate}% submission rate.\n\n—`
    );
    return `mailto:${detail.advisor.email}?subject=${subject}&body=${body}`;
  }, [detail]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f8fafc]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#5b93d6]"></div>
      </div>
    );
  }
  if (!detail) return null;

  const { advisor, kpi, weekly_trend: weeks, timeline, invites, submissions } = detail;

  return (
    <div className="min-h-screen bg-[#f8fafc]" data-testid="advisor-detail-page">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-gradient-to-r from-[#0f2444] via-[#1e3a63] to-[#26518c] shadow-lg">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <Button variant="ghost" size="sm" onClick={() => navigate('/admin/dashboard')} className="text-white hover:bg-white/10" data-testid="detail-back-button">
                <ArrowLeft className="w-4 h-4 mr-2" /> Admin Control Center
              </Button>
              <div className="hidden sm:block h-6 w-px bg-white/20" />
              <img src="/stpk-logo.jpg" alt="SteelPeak" className="w-8 h-8 rounded-md shadow-sm" />
              <div className="min-w-0">
                <h1 className="text-lg font-bold text-white truncate" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="detail-advisor-name">
                  {advisor.name || advisor.email}
                </h1>
                <p className="text-xs text-white/80 truncate">
                  <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] ${advisor.role === 'admin' ? 'bg-purple-500/40' : 'bg-white/15'} mr-2`}>{advisor.role?.toUpperCase()}</span>
                  {advisor.email} · Last active {fmtRelative(advisor.last_active_at)}
                </p>
              </div>
            </div>
            <a href={mailtoHref} data-testid="detail-message-advisor-button">
              <Button className="bg-[#5b93d6] hover:bg-[#4a7dc5] text-white">
                <Mail className="w-4 h-4 mr-2" /> Message Advisor
              </Button>
            </a>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-8 space-y-6">
        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <KpiCard label="Invites Sent" value={kpi.sent} sub={`${kpi.sent_30d} last 30d`} testid="detail-kpi-sent" />
          <KpiCard label="Verified" value={kpi.verified} sub={`${kpi.verify_rate}% verify rate`} subClass="text-[#5b93d6]" />
          <KpiCard label="Submitted" value={kpi.submitted} sub={`${kpi.submission_rate}% submission rate`} subClass="text-green-600" testid="detail-kpi-submitted" />
          <div className="bg-gradient-to-br from-[#0f2444] via-[#1e3a63] to-[#26518c] text-white rounded-lg shadow-sm p-4">
            <p className="text-xs text-white/80 uppercase tracking-wider">Post-Verify Close</p>
            <p className="text-2xl font-bold mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{kpi.post_verify_rate}%</p>
            <p className="text-xs text-white/70 mt-1">of verified clients complete the form</p>
          </div>
        </div>

        {/* Secondary KPI row */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <KpiCard label="In Progress" value={kpi.in_progress} subClass="text-blue-600" />
          <KpiCard label="Pending" value={kpi.pending} subClass="text-yellow-700" />
          <KpiCard label="Expired" value={kpi.expired} />
          <KpiCard label="Revoked" value={kpi.revoked} subClass="text-red-600" />
          <KpiCard label="Median Time" value={fmtHours(kpi.median_hours_to_submit)} sub="invite → submitted" />
        </div>

        {/* Sparkline */}
        <div className="bg-white rounded-lg shadow-sm border border-[#e2e8f0] p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-[#0f172a] flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <CalendarDays className="w-4 h-4 text-[#5b93d6]" /> 8-Week Trend
            </h3>
            <div className="flex items-center gap-3 text-xs text-[#475569]">
              <span className="flex items-center gap-1"><span className="w-3 h-1 rounded-sm bg-[#5b93d6]"></span>Sent</span>
              <span className="flex items-center gap-1"><span className="w-3 h-1 rounded-sm bg-[#0f2444]"></span>Submitted</span>
            </div>
          </div>
          <Sparkline weeks={weeks} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Timeline */}
          <div className="bg-white rounded-lg shadow-sm border border-[#e2e8f0]">
            <div className="px-5 py-4 border-b border-[#e2e8f0]">
              <h3 className="text-sm font-semibold text-[#0f172a] flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                <Activity className="w-4 h-4 text-[#5b93d6]" /> Activity Timeline
              </h3>
            </div>
            <div className="p-5">
              {timeline.length === 0 ? (
                <p className="text-sm text-[#475569] text-center py-8">No activity yet.</p>
              ) : (
                <ol className="relative border-l-2 border-[#e2e8f0] ml-2" data-testid="detail-timeline">
                  {timeline.map((ev, i) => {
                    const meta = KIND_META[ev.kind] || KIND_META.created;
                    const Icon = meta.icon;
                    return (
                      <li key={`${ev.invite_id}-${ev.kind}-${i}`} className="ml-6 mb-5 last:mb-0">
                        <span className={`absolute -left-[13px] flex items-center justify-center w-6 h-6 rounded-full ring-4 ${meta.color} ${meta.ring} text-white`}>
                          <Icon className="w-3 h-3" />
                        </span>
                        <div className="flex items-baseline justify-between gap-2">
                          <p className="text-sm text-[#0f172a]">{ev.label}</p>
                          <time className="text-xs text-[#94a3b8] whitespace-nowrap" title={fmtDateTime(ev.when)}>{fmtRelative(ev.when)}</time>
                        </div>
                      </li>
                    );
                  })}
                </ol>
              )}
            </div>
          </div>

          {/* Submissions list */}
          <div className="bg-white rounded-lg shadow-sm border border-[#e2e8f0]">
            <div className="px-5 py-4 border-b border-[#e2e8f0] flex items-center justify-between">
              <h3 className="text-sm font-semibold text-[#0f172a] flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                <FileText className="w-4 h-4 text-[#5b93d6]" /> Submissions ({submissions.length})
              </h3>
            </div>
            <div className="divide-y divide-[#e2e8f0] max-h-[400px] overflow-y-auto">
              {submissions.length === 0 ? (
                <p className="text-sm text-[#475569] text-center py-8">No submissions yet.</p>
              ) : submissions.map((s) => (
                <div key={s.id} className="px-5 py-3 hover:bg-[#f8fafc] flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-[#0f172a] truncate">{s.client_name || s.client_email || 'Client'}</p>
                    <p className="text-xs text-[#475569]">{fmtDate(s.submitted_at)}</p>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => navigate(`/advisor/submission/${s.id}`)} data-testid={`detail-view-submission-${s.id}`}>
                    <Eye className="w-4 h-4 mr-1" /> View
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Invites table */}
        <div className="bg-white rounded-lg shadow-sm border border-[#e2e8f0] overflow-hidden">
          <div className="px-5 py-4 border-b border-[#e2e8f0]">
            <h3 className="text-sm font-semibold text-[#0f172a] flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <Users className="w-4 h-4 text-[#5b93d6]" /> All Invites ({invites.length})
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#f8fafc] border-b border-[#e2e8f0]">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#475569] uppercase">Client</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#475569] uppercase">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#475569] uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#475569] uppercase">Created</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#475569] uppercase">Expires</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#e2e8f0]" data-testid="detail-invites-table">
                {invites.length === 0 ? (
                  <tr><td colSpan="5" className="px-6 py-8 text-center text-[#475569]">No invites yet.</td></tr>
                ) : invites.map((inv) => (
                  <tr key={inv.id} className="hover:bg-[#f8fafc]" data-testid={`detail-invite-${inv.id}`}>
                    <td className="px-6 py-3 text-sm text-[#0f172a]">{inv.client_name || '—'}</td>
                    <td className="px-6 py-3 text-sm text-[#475569]">{inv.client_email}</td>
                    <td className="px-6 py-3">{statusBadge(inv.status)}</td>
                    <td className="px-6 py-3 text-sm text-[#475569]">{fmtDate(inv.created_at)}</td>
                    <td className="px-6 py-3 text-sm text-[#475569]">{fmtDate(inv.expires_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
